import { createContext, useContext, useRef, useState, useCallback, useEffect } from "react";
import * as Sentry from "@sentry/react";
import { useStore } from "../../stores/store";
import { useAccessControl } from "../../hooks/useAccessControl";
import { useSquawkAudioPrompts } from "../../hooks/useSquawkAudioPrompts";
import { useGetSquawkConnectionType } from "./useGetSquawkConnectionType";
import { useGetSquawkPlayerV2 } from "./useGetSquawkPlayerV2";
import { useWebRTCPlayer, MAX_RTC_RETRIES } from "./webrtc/useWebRTCPlayer";
import { useMSEPlayer, MAX_MSE_RETRIES } from "./mse/useMSEPlayer";
import { useShoutcastPlayer, MAX_SHOUTCAST_RETRIES } from "./shoutcast/useShoutcastPlayer";
import { useDemoSquawkPlayer } from "./demo/useDemoSquawkPlayer";
import { useBackgroundAudioKeepAlive } from "./mse/useBackgroundAudioKeepAlive";
import { useSquawkV2Player } from "./v2/useSquawkV2Player";
import { trackFeatureUsage } from "../../utils/gtm";
import { diagnosticEventLog } from "../../shared/services/diagnostic";
import { channels } from "./squawkChannels";

// Create context
const SquawkPlayerContext = createContext(null);

/**
 * SquawkPlayerProvider - Centralized audio player state and connection management
 *
 * This provider:
 * - Maintains a single, persistent audio connection
 * - Never unmounts (stays alive during layout changes)
 * - Provides shared state and methods to multiple UI components
 * - Prevents duplicate connections and echo issues
 */
export const SquawkPlayerProvider = ({ children }) => {
  const selectedChannel = useStore((s) => s.selectedChannel);
  const setSelectedChannel = useStore((s) => s.setSelectedChannel);

  const { authenticated, isInDemoMode } = useAccessControl();
  const { data: connectionType } = useGetSquawkConnectionType();
  const { data: isPlayerV2Enabled = false } = useGetSquawkPlayerV2();

  const [volume, setVolume] = useState(1);

  // Restore persisted channel on mount
  useEffect(() => {
    try {
      const persistedName = localStorage.getItem("squawk.selectedChannel");
      if (persistedName && !selectedChannel) {
        const channel = channels.find((c) => c.name === persistedName);
        if (channel) {
          diagnosticEventLog.record("squawk:audio", "info", "channel-restore", { channel: persistedName });
          setSelectedChannel(channel);
        } else {
          localStorage.removeItem("squawk.selectedChannel");
        }
      }
    } catch {
      // localStorage unavailable
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debouncing: Track last channel selection time to prevent rapid switching
  const lastChannelSelectTime = useRef(0);
  const CHANNEL_SELECT_DEBOUNCE_MS = 300;

  // Persistent refs for audio elements - these never unmount
  const rtcElRef = useRef(null);
  const mseElRef = useRef(null);
  const mseKeepAliveElRef = useRef(null);
  const shoutcastElRef = useRef(null);
  const demoElRef = useRef(null);

  // Determine which connection type to use
  const useWebRTC = !connectionType || connectionType === "web_rtc";
  const useShoutcast = connectionType === "shoutcast";

  // Preventive multi-player protection: Only enable one player at a time
  // When switching connection types, ensure the old player is fully disabled before enabling the new one
  const [enabledPlayer, setEnabledPlayer] = useState(
    useWebRTC ? "webrtc" : useShoutcast ? "shoutcast" : "mse"
  );

  // Session-scoped fallback override — when a player exhausts retries, auto-fall to the next.
  // Not persisted to user preferences (they chose their connection type for a reason).
  // null = no override, use user preference; "mse" | "shoutcast" = active fallback
  const [fallbackOverride, setFallbackOverride] = useState(null);
  const fallbackAttemptedRef = useRef(new Set());

  // Update enabled player when connection type or fallback changes
  useEffect(() => {
    const preferredPlayer = useWebRTC ? "webrtc" : useShoutcast ? "shoutcast" : "mse";
    const targetPlayer = fallbackOverride || preferredPlayer;

    // If switching players, briefly disable all to ensure clean transition
    if (enabledPlayer !== targetPlayer) {
      console.log(`[SquawkPlayerContext] Switching from ${enabledPlayer} to ${targetPlayer}${fallbackOverride ? " (auto-fallback)" : ""}`);
      diagnosticEventLog.record("squawk:audio", "info", "player-switch", { from: enabledPlayer, to: targetPlayer, fallback: !!fallbackOverride });
      // First disable current player
      setEnabledPlayer(null);
      // Then enable new player after a brief delay
      // During this 100ms gap:
      // - All players are disabled (enabled=false)
      // - All players transition to "idle" state
      // - No audio plays (prevents echo during transition)
      // - This is INTENTIONAL and ensures clean handoff
      const timer = setTimeout(() => {
        setEnabledPlayer(targetPlayer);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [useWebRTC, useShoutcast, enabledPlayer, fallbackOverride]);

  // Use WebRTC player hook - only enabled when explicitly set and v2 is not active
  const webRTCPlayer = useWebRTCPlayer({
    audioElement: rtcElRef.current,
    address: selectedChannel?.address || null,
    enabled: !isPlayerV2Enabled && enabledPlayer === "webrtc" && !!selectedChannel,
    authenticated,
  });

  // Use MSE player hook - only enabled when explicitly set and v2 is not active
  const msePlayer = useMSEPlayer({
    audioElement: mseElRef.current,
    address: selectedChannel?.address || null,
    enabled: !isPlayerV2Enabled && enabledPlayer === "mse" && !!selectedChannel,
    authenticated,
  });

  // Silent audio keepalive — prevents Chrome from pausing the MSE <video> element
  // in background tabs ("video-only background media" power-saving).
  useBackgroundAudioKeepAlive({
    audioElement: mseKeepAliveElRef.current,
    active: enabledPlayer === "mse" && msePlayer.status === "connected",
  });

  // Use Shoutcast player hook - HTTP audio stream, background-safe, disabled when v2 is active
  const shoutcastPlayer = useShoutcastPlayer({
    audioElement: shoutcastElRef.current,
    address: selectedChannel?.address || null,
    enabled: !isPlayerV2Enabled && enabledPlayer === "shoutcast" && !!selectedChannel,
    authenticated,
  });

  // Use Demo player hook - for logged-out users and non-subscribers to play historical recordings
  // TODO(demo-playback): Re-enable when backend audio serving is restored.
  // Forcing enabled: false prevents any initialisation even if a channel was
  // persisted from a previous session or a user's state gets into an odd shape.
  const DEMO_PLAYBACK_ENABLED = false;
  const demoPlayer = useDemoSquawkPlayer({
    audioElement: demoElRef.current,
    channel: selectedChannel?.demoChannel || null,
    enabled: DEMO_PLAYBACK_ENABLED && isInDemoMode && !!selectedChannel,
  });

  // Use V2 player hook - WebSocket + MSE streaming via audio-v2 backend (internal users only)
  const v2Player = useSquawkV2Player({
    v2Keyword: selectedChannel?.v2Keyword || null,
    enabled: isPlayerV2Enabled && !isInDemoMode && !!selectedChannel,
    authenticated,
  });

  // Auto-fallback chain: WebRTC → MSE → Shoutcast
  // When a player exhausts retries, automatically switch to the next in the chain.
  // Session-scoped only — resets on channel deselect/reselect.
  useEffect(() => {
    // Only trigger when a channel is selected and v2 is not in use
    if (!selectedChannel || isPlayerV2Enabled || isInDemoMode) return;

    const FALLBACK_CHAIN = { webrtc: "mse", mse: "shoutcast" };

    // WebRTC exhausted → fall to MSE
    if (
      enabledPlayer === "webrtc" &&
      webRTCPlayer.status === "error" &&
      webRTCPlayer.retryCount >= MAX_RTC_RETRIES &&
      !fallbackAttemptedRef.current.has("webrtc→mse")
    ) {
      fallbackAttemptedRef.current.add("webrtc→mse");
      console.warn("[SquawkPlayerContext] WebRTC exhausted retries — auto-fallback to MSE");
      diagnosticEventLog.record("squawk:audio", "warn", "auto-fallback", { from: "webrtc", to: "mse" });
      Sentry.captureMessage("Squawk auto-fallback: WebRTC → MSE", {
        level: "warning",
        tags: { component: "SquawkPlayerContext", context: "auto_fallback", from_player: "webrtc", to_player: "mse" },
        extra: { lastError: webRTCPlayer.error?.message, retryCount: webRTCPlayer.retryCount, channel: selectedChannel?.name },
      });
      setFallbackOverride("mse");
      return;
    }

    // MSE exhausted → fall to Shoutcast
    if (
      enabledPlayer === "mse" &&
      fallbackOverride === "mse" && // only if we're here via fallback
      msePlayer.status === "error" &&
      msePlayer.retryCount >= MAX_MSE_RETRIES &&
      !fallbackAttemptedRef.current.has("mse→shoutcast")
    ) {
      fallbackAttemptedRef.current.add("mse→shoutcast");
      console.warn("[SquawkPlayerContext] MSE exhausted retries — auto-fallback to Shoutcast");
      diagnosticEventLog.record("squawk:audio", "warn", "auto-fallback", { from: "mse", to: "shoutcast" });
      Sentry.captureMessage("Squawk auto-fallback: MSE → Shoutcast", {
        level: "warning",
        tags: { component: "SquawkPlayerContext", context: "auto_fallback", from_player: "mse", to_player: "shoutcast" },
        extra: { lastError: msePlayer.error?.message, retryCount: msePlayer.retryCount, channel: selectedChannel?.name },
      });
      setFallbackOverride("shoutcast");
    }
  }, [
    enabledPlayer, fallbackOverride, selectedChannel,
    webRTCPlayer.status, webRTCPlayer.retryCount,
    msePlayer.status, msePlayer.retryCount,
    isPlayerV2Enabled, isInDemoMode,
  ]);

  // Safety check: ensure multiple players are never actively connecting/connected simultaneously
  // This should never happen due to the preventive enabledPlayer state,
  // but provides a fail-safe that logs critical errors
  useEffect(() => {
    const activePlayers = [
      webRTCPlayer.status === "connecting" || webRTCPlayer.status === "connected" ? "webrtc" : null,
      msePlayer.status === "connecting" || msePlayer.status === "connected" ? "mse" : null,
      shoutcastPlayer.status === "connecting" || shoutcastPlayer.status === "connected" ? "shoutcast" : null,
    ].filter(Boolean);

    if (activePlayers.length > 1) {
      console.error(
        `[SquawkPlayerContext] CRITICAL: Multiple players active simultaneously: ${activePlayers.join(", ")}! ` +
        "This should never happen with preventive checks. Forcing cleanup."
      );
      diagnosticEventLog.record("squawk:audio", "error", "multi-player-critical", {
        webrtcStatus: webRTCPlayer.status,
        mseStatus: msePlayer.status,
        shoutcastStatus: shoutcastPlayer.status,
      });
      setSelectedChannel(null);
      setEnabledPlayer(null);
    }
  }, [webRTCPlayer.status, msePlayer.status, shoutcastPlayer.status, setSelectedChannel]);

  // Get the active player's status
  // Note: activePlayer is determined by useWebRTC (connection type), not enabledPlayer state
  // During the 100ms transition when enabledPlayer=null, both players are disabled and return "idle"
  // This is correct behavior - we read "idle" from the player matching the target connection type
  // When in demo mode (unauthenticated or non-subscriber), use demo player for historical recordings
  // When v2 is enabled (internal users), use v2 player instead of v1 players
  const activePlayer = isInDemoMode
    ? demoPlayer
    : isPlayerV2Enabled
    ? v2Player
    : fallbackOverride === "mse"
    ? msePlayer
    : fallbackOverride === "shoutcast"
    ? shoutcastPlayer
    : useWebRTC
    ? webRTCPlayer
    : useShoutcast
    ? shoutcastPlayer
    : msePlayer;
  const status = activePlayer.status;

  // Log status changes for diagnostics
  useEffect(() => {
    diagnosticEventLog.record("squawk:audio", "info", "status-change", { status, enabledPlayer, channel: selectedChannel?.name ?? null });
  }, [status, enabledPlayer, selectedChannel?.name]);

  // Plays audio prompts on status change
  useSquawkAudioPrompts(status, selectedChannel?.name ?? "", { isInDemoMode });

  // Channel selection handler
  const handleChannelSelect = useCallback(
    (channel) => {
      // Check if user is deselecting (stopping) the current channel
      const isDeselecting = selectedChannel?.name === channel.name;

      // Debounce: Prevent rapid channel switching that could cause race conditions
      // CRITICAL: Do NOT debounce deselection - users must be able to stop audio instantly
      if (!isDeselecting) {
        const now = Date.now();
        const timeSinceLastSelect = now - lastChannelSelectTime.current;

        if (timeSinceLastSelect < CHANNEL_SELECT_DEBOUNCE_MS) {
          console.log(`[SquawkPlayerContext] Debouncing channel switch (${timeSinceLastSelect}ms since last select)`);
          diagnosticEventLog.record("squawk:audio", "warn", "channel-debounced", { timeSinceLastMs: timeSinceLastSelect });
          return;
        }

        lastChannelSelectTime.current = now;
      }

      // Prevent spam-clicking to RE-SELECT the same channel while it's already connecting
      // IMPORTANT: This should NOT block deselection - users must always be able to stop audio
      // Since clicking same channel = toggle/deselect, this check should never block when isDeselecting
      if (
        status === "connecting" &&
        selectedChannel?.name === channel.name &&
        !isDeselecting  // CRITICAL: Allow deselection even during connecting!
      ) {
        // This case would only occur if somehow we're trying to "re-select" while connecting
        // which shouldn't happen with toggle logic, but keeping as safeguard
        return;
      }

      // Toggle channel selection
      if (isDeselecting) {
        // Deselect channel - this will trigger cleanup in the hooks
        // This is ALWAYS allowed immediately (no debounce) for good UX
        setSelectedChannel(null);
        setFallbackOverride(null);
        fallbackAttemptedRef.current.clear();
        try { localStorage.removeItem("squawk.selectedChannel"); } catch {}
        diagnosticEventLog.record("squawk:audio", "info", "channel-deselect", { channel: channel.name });
        trackFeatureUsage({ feature_name: "squawk_player", feature_action: "channel_deselect" });
        // Don't force remount here - it causes race conditions with disconnect
        // The existing element will be reused for next connection
      } else {
        // Select new channel - this will trigger connection in the hooks
        setSelectedChannel(channel);
        setFallbackOverride(null);
        fallbackAttemptedRef.current.clear();
        try { localStorage.setItem("squawk.selectedChannel", channel.name); } catch {}
        diagnosticEventLog.record("squawk:audio", "info", "channel-select", { channel: channel.name, connectionType, isDemoMode: isInDemoMode });
        trackFeatureUsage({ feature_name: "squawk_player", feature_action: "channel_select", feature_detail: channel.name });
      }
    },
    [selectedChannel, status, setSelectedChannel]
  );

  // Volume handler - applies to all audio elements directly for stability
  // Uses refs instead of player objects to avoid unnecessary re-creation
  const handleVolumeChange = useCallback(
    (newVolume) => {
      setVolume(newVolume);

      // Set volume directly on audio elements for consistency
      // This prevents issues when switching connection types
      // Using refs makes this callback stable (no dependencies)
      try {
        if (rtcElRef.current) {
          rtcElRef.current.volume = newVolume;
        }
      } catch (e) {
        console.debug("Error setting WebRTC volume:", e);
      }

      try {
        if (mseElRef.current) {
          mseElRef.current.volume = newVolume;
        }
      } catch (e) {
        console.debug("Error setting MSE volume:", e);
      }

      try {
        if (shoutcastElRef.current) {
          shoutcastElRef.current.volume = newVolume;
        }
      } catch (e) {
        console.debug("Error setting Shoutcast volume:", e);
      }

      try {
        if (demoElRef.current) {
          demoElRef.current.volume = newVolume;
        }
      } catch (e) {
        console.debug("Error setting demo volume:", e);
      }

      // V2 player uses GainNode — delegate via hook's setVolume
      try {
        v2Player.setVolume(newVolume);
      } catch (e) {
        console.debug("Error setting V2 volume:", e);
      }
    },
    [v2Player.setVolume] // v2Player.setVolume is a stable useCallback
  );

  // Context value
  const value = {
    // State
    selectedChannel,
    status,
    volume,
    connectionType,

    // Methods
    handleChannelSelect,
    handleVolumeChange,

    // For debugging
    retryCount: activePlayer.retryCount ?? 0,
    error: activePlayer.error,

    // Demo mode flag
    isDemoMode: isInDemoMode,

    // Player v2 flag (internal users only)
    isPlayerV2Enabled,
  };

  return (
    <SquawkPlayerContext.Provider value={value}>
      {children}

      {/* Hidden audio elements - always mounted, never unmount */}
      <audio
        id="webrtc-audio"
        autoPlay
        ref={rtcElRef}
        style={{ display: 'none' }}
      />

      <video
        id="flussonic-mse-video"
        ref={mseElRef}
        style={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          top: '-10000px',
          left: '-10000px',
          zIndex: -10,
        }}
      />
      <audio
        id="mse-background-keepalive"
        ref={mseKeepAliveElRef}
        style={{ display: 'none' }}
      />

      <audio
        id="shoutcast-audio"
        ref={shoutcastElRef}
        style={{ display: 'none' }}
      />

      <audio
        id="demo-audio"
        ref={demoElRef}
        style={{ display: 'none' }}
      />
    </SquawkPlayerContext.Provider>
  );
};

/**
 * Hook to access squawk player context
 */
export const useSquawkPlayer = () => {
  const context = useContext(SquawkPlayerContext);
  if (!context) {
    throw new Error("useSquawkPlayer must be used within SquawkPlayerProvider");
  }
  return context;
};
